import { useState, useEffect, useCallback } from 'react';
import { Mail, Phone, MessageCircle, CheckCircle, XCircle, UserPlus, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { leadStatusLabels, getWhatsAppLink } from '../../lib/utils';

export default function AdminLeads() {
  const { api } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await api.get(`/leads?${params.toString()}`);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Erro ao carregar leads.');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await api.patch(`/leads/${leadId}/status`, { status: newStatus });
      toast.success('Status atualizado!');
      fetchLeads();
    } catch (error) {
      toast.error('Erro ao atualizar status.');
    }
  };

  const handleConvertToClient = async (leadId) => {
    try {
      await api.post(`/leads/${leadId}/convert`);
      toast.success('Lead convertido em cliente!');
      fetchLeads();
    } catch (error) {
      toast.error('Erro ao converter lead.');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      handled: 'bg-green-100 text-green-700',
      spam: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const statuses = ['new', 'handled', 'spam'];

  return (
    <div className="space-y-6" data-testid="admin-leads">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800">Leads</h1>
          <p className="text-slate-500 mt-1">
            {leads.filter((l) => l.status === 'new').length} leads aguardando tratamento
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {statuses.map((s) => (
                  <SelectItem key={s} value={s}>{leadStatusLabels[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leads List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
        </div>
      ) : leads.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nenhum lead encontrado.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {leads.map((lead) => (
            <Card key={lead.id} className={`hover:shadow-md transition-shadow ${lead.status === 'new' ? 'border-l-4 border-l-blue-500' : ''}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-purple-600" />
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg text-slate-800">{lead.name}</h3>
                      <Badge className={getStatusColor(lead.status)}>
                        {leadStatusLabels[lead.status]}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-2">
                      {lead.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {lead.phone}
                        </span>
                      )}
                      {lead.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {lead.email}
                        </span>
                      )}
                    </div>
                    {lead.message && (
                      <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                        "{lead.message}"
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                      <span>Recebido em {new Date(lead.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      {lead.page_source && <span>• Origem: {lead.page_source}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {lead.phone && (
                      <Button variant="outline" size="sm" asChild className="text-green-600">
                        <a href={getWhatsAppLink(lead.phone, `Olá ${lead.name}! Vi sua mensagem em nosso site.`)} target="_blank" rel="noopener noreferrer">
                          <MessageCircle className="w-4 h-4 mr-1" />
                          WhatsApp
                        </a>
                      </Button>
                    )}
                    {lead.status === 'new' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(lead.id, 'handled')}
                          className="text-green-600"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Tratado
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(lead.id, 'spam')}
                          className="text-red-500"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Spam
                        </Button>
                      </>
                    )}
                    {lead.status !== 'spam' && (
                      <Button
                        size="sm"
                        onClick={() => handleConvertToClient(lead.id)}
                        className="bg-brand-blue hover:bg-brand-blue-dark"
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Converter em Cliente
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
